import type { ProjectDTO, StepDTO } from '@pace/contracts';
import { addDays, chainOf, STATUS_RANK, type ProjectView, type StepView } from '@pace/domain';
import type { Lookups } from '../data/bootstrap';

export interface WorkItem {
  project: ProjectDTO;
  step: StepDTO;
  view: StepView;
  /** Stage just before this one in the chain (for "after Heat treatment"). */
  prev: StepDTO | null;
}

export interface MyWork {
  overdue: WorkItem[];
  today: WorkItem[];
  hold: WorkItem[];
  week: WorkItem[];
  later: WorkItem[];
  done: WorkItem[];
}

/**
 * A stage owner's work: open steps of their departments on live projects, grouped the
 * way the day is planned — overdue, due today, on hold, the next 7 days, later — plus
 * what they finished in the last 30 days.
 */
export function myWork(
  projects: ProjectDTO[],
  views: Map<string, ProjectView>,
  lk: Lookups,
  userId: string,
  onlyDept?: string | null,
): MyWork {
  const mine = new Set(lk.user(userId)?.departmentIds ?? []);
  const today = lk.today();
  const weekEnd = addDays(today, 7);
  const out: MyWork = { overdue: [], today: [], hold: [], week: [], later: [], done: [] };
  for (const p of projects) {
    if (p.phase !== 'active') continue;
    const v = views.get(p.id);
    if (!v) continue;
    for (const s of p.steps) {
      const dept = lk.deptOf(s);
      if (!dept || !mine.has(dept) || (onlyDept && dept !== onlyDept)) continue;
      const c = chainOf(v, s.id);
      if (!c) continue;
      const sv = c.chain[c.index]!;
      const before = c.chain
        .slice(0, c.index)
        .reverse()
        .find((x) => x.state !== 'skipped');
      const item: WorkItem = {
        project: p,
        step: s,
        view: sv,
        prev: before ? p.steps.find((x) => x.id === before.id)! : null,
      };
      if (s.state === 'skipped') continue;
      if (s.state === 'done') {
        if (s.actual && s.actual >= addDays(today, -30)) out.done.push(item);
      } else if (sv.status === 'OVERDUE') out.overdue.push(item);
      else if (sv.status === 'DUE_TODAY') out.today.push(item);
      else if (sv.status === 'HOLD') out.hold.push(item);
      else if (sv.ref && sv.ref <= weekEnd) out.week.push(item);
      else out.later.push(item);
    }
  }
  const byDate = (a: WorkItem, b: WorkItem) =>
    (a.view.ref ?? '').localeCompare(b.view.ref ?? '') ||
    STATUS_RANK[a.view.status] - STATUS_RANK[b.view.status] ||
    a.project.partName.localeCompare(b.project.partName);
  out.overdue.sort(byDate);
  out.today.sort(byDate);
  out.hold.sort(byDate);
  out.week.sort(byDate);
  out.later.sort((a, b) => (a.view.forecast ?? '').localeCompare(b.view.forecast ?? ''));
  out.done.sort((a, b) => (b.step.actual ?? '').localeCompare(a.step.actual ?? ''));
  return out;
}
